// Site and constraints on an ordered property (Section 2 continued of the Solar Opportunity Report,
// 10-serverless/120-pack/render.py s2c; Neil, 10 Oct 2026: "what else would a commercial PV person be
// interested in? height of buildings, access ..."). From the API's site block (100-api common/site.py):
// the headline figures, planning constraints, the local grid's spare capacity, the building's height
// and access, the roof's aspect and sun, what's around the building, and what to check at the survey.
// Airports have their own section next (AirportsSection); funding is on Grants and funding.
// Never names where the facts come from.
import { Car, ClipboardCheck, Compass, Home, Landmark, Ruler, Zap } from "lucide-react";
import type { PreliminaryCheck, SiteBlock } from "../api";
import { cap } from "./packFormat";

type RoofAi = NonNullable<NonNullable<PreliminaryCheck["ai"]>["roof"]>;

export function SiteFacts({ s, kicker, roofAi }: { s: SiteBlock; kicker?: string; roofAi?: RoofAi | null }) {
  const r = s.roof;
  const c = s.constraints;
  const g = s.grid;
  const listed = c?.listed ?? [];
  const areas = c?.conservationAreas ?? [];
  const av = s.extras?.airports;
  // The planning figure: the strongest constraint on or next to the site
  const planningFig = listed.length ? "Listed nearby" : areas.length ? "Conservation area" : "No constraints";
  return (
    <section className="card eo" aria-labelledby="site-title">
      <header className="airoof-head">
        <span className="airoof-mark" aria-hidden="true">
          <Home size={20} />
        </span>
        <div className="airoof-title">
          {kicker && <p className="pk-kicker">{kicker}</p>}
          <h2 id="site-title">Site and constraints</h2>
          <p className="muted">
            What an installer needs to know about the site before a survey: planning, the local electricity network, the building's height and access, and the roof's
            aspect and sun.
          </p>
        </div>
      </header>

      {/* The headline figures, as in the report */}
      <div className="eo-stats">
        <div className={listed.length || areas.length ? "is-dark" : undefined}>
          <b>{planningFig}</b>
          <span>planning on or next to the site</span>
        </div>
        <div>
          <b>{g?.covered && g.headroomMw != null ? `${g.headroomMw} MW` : "—"}</b>
          <span>spare grid capacity for generation</span>
        </div>
        <div>
          <b>{r?.heightM != null ? `~${r.heightM} m` : "—"}</b>
          <span>building height</span>
        </div>
        <div>
          <b>{r?.sunnyPct != null ? `${r.sunnyPct}%` : "—"}</b>
          <span>of the roof in strong sun</span>
        </div>
      </div>

      <div className="pm-two site-two">
        {/* Planning constraints */}
        <div className="eo-block">
          <h3>
            <Landmark size={16} aria-hidden="true" /> Planning
          </h3>
          {!c?.checked ? (
            <p className="muted">Not checked yet.</p>
          ) : (
            <ul className="site-list">
              {listed.length > 0 ? (
                listed.map((l) => (
                  <li key={l.name} className="is-warn">
                    <b>Listed building{l.grade ? `, Grade ${l.grade}` : ""}</b> on or next to the site: {l.name}. Panels close to it may need listed building consent.
                  </li>
                ))
              ) : (
                <li className="is-ok">No listed buildings on or next to the site.</li>
              )}
              {areas.length > 0 ? (
                areas.map((a) => (
                  <li key={a} className="is-warn">
                    <b>Conservation area:</b> {a}. Panels on roofs facing a road may need planning permission.
                  </li>
                ))
              ) : (
                <li className="is-ok">Not in a conservation area.</li>
              )}
              {c.monuments.map((m) => (
                <li key={m} className="is-warn">
                  <b>Scheduled monument nearby:</b> {m}.
                </li>
              ))}
              <li className="muted">Rooftop solar on non-domestic buildings is usually permitted development; check the limits for this one with the local planning authority.</li>
            </ul>
          )}
        </div>

        {/* The local grid */}
        <div className="eo-block">
          <h3>
            <Zap size={16} aria-hidden="true" /> Grid connection
          </h3>
          {!g?.covered ? (
            <p className="muted">Grid capacity for this area isn't covered yet.</p>
          ) : (
            <ul className="site-list">
              <li className={g.colour === "green" ? "is-ok" : g.colour === "red" ? "is-bad" : "is-warn"}>
                <b>{g.substation}</b> substation ({g.voltageKv} kV): {g.headroomMw != null ? <><b>{g.headroomMw} MW</b> spare for generation</> : "capacity unknown"}
                {g.colour ? `, rated ${g.colour}` : ""}.
              </li>
              {g.upstreamConstraint && (
                <li className="is-warn">A constraint further up the network: larger systems may face conditions or a wait for connection.</li>
              )}
              <li className="muted">Network operator: {g.operator}. Confirm with a connection application (G99) for the system size.</li>
            </ul>
          )}
        </div>
      </div>

      <div className="pm-two site-two">
        {/* How tall, and how to get onto the roof */}
        <div className="eo-block">
          <h3>
            <Ruler size={16} aria-hidden="true" /> Height and access
          </h3>
          {r ? (
            <ul className="site-list">
              <li>
                {r.heightM != null ? `About ${r.heightM} m` : "Height not known"}
                {r.storeys ? `, about ${r.storeys} storeys` : ""}
                {r.heightM != null ? ` (${r.heightHow === "measured" ? "measured" : "estimated from its floors"})` : ""}.
              </li>
              {r.access && <li>{r.access}</li>}
              {roofAi?.access && <li>{cap(roofAi.access)}</li>}
            </ul>
          ) : (
            <p className="muted">Not known yet.</p>
          )}
        </div>
        {/* Which way the roof faces, and its sun (also in Section 7, Roof and panel plan) */}
        <RoofFacing r={r} />
      </div>

      {/* Around the building: more than the roof, and the site's risks (worked out once, in the background) */}
      <div className="eo-block">
        <h3>
          <Car size={16} aria-hidden="true" /> Around the building
        </h3>
        {!s.extras ? (
          <p className="pk-notfound">We're checking the car parks, flood risk and airports around the building. Refresh in a minute.</p>
        ) : (
          <ul className="site-list">
            {s.extras.outdoor && (
              <li className={s.extras.outdoor.canopyKwp || s.extras.outdoor.groundKwp ? "is-ok" : "muted"}>
                {s.extras.outdoor.canopyKwp ? (
                  <>
                    <b>Solar car park canopies: about {s.extras.outdoor.canopyKwp.toLocaleString("en-GB")} kWp</b> over{" "}
                    {s.extras.outdoor.carParkTotalM2.toLocaleString("en-GB")} m² of car park (about{" "}
                    {s.extras.outdoor.carParks.reduce((n, cp) => n + cp.spaces, 0).toLocaleString("en-GB")} spaces), on top of the roof.
                  </>
                ) : (
                  s.extras.outdoor.note
                )}
                {s.extras.outdoor.groundKwp ? (
                  <>
                    {" "}
                    <b>Open ground: about {s.extras.outdoor.groundKwp.toLocaleString("en-GB")} kWp</b> on {s.extras.outdoor.openGroundM2.toLocaleString("en-GB")} m² next to the building
                    (check who owns it).
                  </>
                ) : null}
              </li>
            )}
            {s.extras.flood && <li className={s.extras.flood.zone === "3" ? "is-bad" : s.extras.flood.zone === "2" ? "is-warn" : s.extras.flood.checked ? "is-ok" : "muted"}>{s.extras.flood.note}</li>}
            {s.extras.solarNearby && (
              <li className="muted">
                {s.extras.solarNearby.note}
                {s.extras.solarNearby.named.length > 0 && ` Nearest: ${s.extras.solarNearby.named.slice(0, 3).map((n) => `${n.name} (${n.km} km)`).join(", ")}.`}
              </li>
            )}
            {/* Airports have their own section next, with the working */}
            {av && (
              <li className={av.glareCheck ? "is-warn" : "is-ok"}>
                {av.airports?.length ? (
                  <>
                    Airports and flight paths: see <a href="#pk-airports">the next section</a>.
                  </>
                ) : (
                  av.note
                )}
              </li>
            )}
            <li className="muted">Wind and snow loads and the roof's fire rating aren't checked here: confirm them at the structural survey.</li>
          </ul>
        )}
      </div>

      {/* What to do at the survey, as the report's callout */}
      <div className="pk-callout">
        <ClipboardCheck size={16} aria-hidden="true" />
        <span>
          <b>For the survey:</b> {listed.length || areas.length ? "check the listed building and conservation rules before design; " : ""}confirm the grid connection for
          the chosen size{g?.upstreamConstraint ? " early, given the constraint further up the network" : ""}, and plan roof access
          {roofAi?.asbestos === "possible" ? " (have the roof tested for asbestos first)" : ""}.
        </span>
      </div>
    </section>
  );
}

/**
 * Which way the roof faces and how much sun it gets: shown here (Section 2 continued, the site) and
 * again in Section 7, Roof and panel plan (Neil, 14 Oct 2026), so the plan has the roof's aspect,
 * sun and pitch next to its panel layout.
 */
export function RoofFacing({ r }: { r: SiteBlock["roof"] | null | undefined }) {
  return (
    <div className="eo-block">
      <h3>
        <Compass size={16} aria-hidden="true" /> Roof facing and sun
      </h3>
      {r ? (
        <ul className="site-list">
          <li>{r.faces.length ? r.faces.map((f) => `${cap(f.face)} ${f.pct}%`).join(", ") : "Flat: panels can face south"}.</li>
          {r.sunnyPct != null && <li>{r.sunnyPct}% of the roof gets strong sun (little shading).</li>}
          <li>
            {r.flatPct}% of the roof is flat{r.pitchRange ? `; pitched parts ${r.pitchRange[0]}–${r.pitchRange[1]}°` : ""}.
          </li>
          <li className="muted">
            {r.planes} roof plane{r.planes === 1 ? "" : "s"}
            {r.roofM2 ? `, about ${r.roofM2.toLocaleString("en-GB")} m² of roof` : ""}
            {r.maxPanels != null ? `; room for ${r.maxPanels.toLocaleString("en-GB")} panels, up to ${r.capacityKwp} kWp with your share left clear` : ""}.
          </li>
        </ul>
      ) : (
        <p className="muted">Not known yet.</p>
      )}
    </div>
  );
}
